import { useQuery } from "@tanstack/react-query";
import { getTrendStats } from "services/dashboard.service";

const seriesKey = (point) =>
  point.date || (point.date_from && `${point.date_from}_${point.date_to}`) || point.label;

// payout.series and collections.series are separate arrays keyed by the same
// time buckets — merge them into one series of { label, collections, payout }
// so the chart can plot both metrics on a shared x-axis.
const mergeSeries = (payout, collections) => {
  const points = new Map();

  (collections?.series || []).forEach((point) => {
    points.set(seriesKey(point), { label: point.label, collections: point.volume, payout: 0 });
  });

  (payout?.series || []).forEach((point) => {
    const key = seriesKey(point);
    const existing = points.get(key) || { label: point.label, collections: 0 };
    points.set(key, { ...existing, payout: point.volume });
  });

  return Array.from(points.values());
};

// period: 7d | 14d | 30d | 60d | 90d (default 30d)
export const useTrendStats = (period = "30d", options = {}) => {
  return useQuery({
    queryKey: ["trend-stats", period],
    queryFn: () => getTrendStats(period),
    select: (res) => {
      const data = res?.data;
      if (!data) return null;

      const { payout, collections } = data;
      return {
        period: data.period,
        granularity: collections?.granularity || payout?.granularity,
        label: collections?.label || payout?.label,
        series: mergeSeries(payout, collections),
        summary: {
          total_collections: collections?.summary?.total_volume ?? 0,
          total_payout: payout?.summary?.total_volume ?? 0,
          peak_label: collections?.summary?.peak_label || payout?.summary?.peak_label,
        },
      };
    },
    ...options,
  });
};
